"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Mail, Lock, Eye, FileText, RefreshCw } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
export default function Privacy() {
  const shouldReduceMotion = useReducedMotion();
  const sections = [{
    id: "scope",
    title: "Introduction and scope",
    icon: ShieldCheck,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            E•Leete Travel Design, operating under E•Leete Group ("we", "us", or "our"), is committed to safeguarding the privacy and personal data of our clients and site visitors. This Privacy Policy details the types of personal information we collect through our website, consultation intake forms, and trip planning engagements, as well as how that information is handled, secured, and retained.
          </p>
          <p className="mt-3 text-foreground/90 leading-relaxed">
            By accessing our services, requesting an Anchor &amp; Orbit itinerary consultation, or interacting with our digital touchpoints, you acknowledge the data handling practices outlined in this policy.
          </p>
        </>
  }, {
    id: "information-collected",
    title: "Information we collect",
    icon: Eye,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            To curate bespoke multi-generational milestone journeys with logistical precision, we gather relevant information directly from you and automatically via your digital engagement:
          </p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-foreground/90">
            <li>
              <strong className="font-medium text-foreground">Contact details:</strong> Full name, email address, telephone number, residential address, and preferred methods of communication.
            </li>
            <li>
              <strong className="font-medium text-foreground">Milestone and travel profiles:</strong> Travel dates, family group dynamics, guest names and ages, dietary needs, mobility considerations, passport details, and destination preferences.
            </li>
            <li>
              <strong className="font-medium text-foreground">Financial information:</strong> Payment transaction metadata and billing records for travel deposits and designer retainers (processed via accredited PCI-compliant gateways).
            </li>
            <li>
              <strong className="font-medium text-foreground">Browsing and device data:</strong> IP addresses, browser specifications, operating system, referring URLs, and page navigation patterns collected via automated logs.
            </li>
          </ul>
        </>
  }, {
    id: "how-we-use",
    title: "How your information is used",
    icon: FileText,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            We use collected data solely to deliver, improve, and secure our luxury travel advisory services:
          </p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-foreground/90">
            <li>Designing bespoke multi-generational itineraries and booking vetted hospitality partners.</li>
            <li>Communicating schedule adjustments, logistical updates, and pre-departure briefings.</li>
            <li>Managing consultation requests, fee payments, and contractual agreements.</li>
            <li>Evaluating site usability and refining our digital experience.</li>
            <li>Complying with aviation, maritime, and border security requirements.</li>
          </ul>
        </>
  }, {
    id: "cookies-tracking",
    title: "Cookies and tracking technologies",
    icon: RefreshCw,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            Our website uses session cookies, functional cookies, and analytics tags to improve page performance, remember client preferences, and assess traffic flows.
          </p>
          <p className="mt-3 text-foreground/90 leading-relaxed">
            You can modify your browser settings to refuse cookies or prompt you before accepting them. Disabling cookies may affect the usability of interactive inquiry elements or client portal features.
          </p>
        </>
  }, {
    id: "sharing-providers",
    title: "Third-party sharing and service providers",
    icon: Lock,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            We never sell, rent, or trade your personal information. To fulfill our travel design commitments, select data is shared strictly under confidentiality agreements with:
          </p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-foreground/90">
            <li>
              <strong className="font-medium text-foreground">Hospitality and transit partners:</strong> Private aviation operators, charter vessels, boutique properties, ground transportation providers, and local expedition leads.
            </li>
            <li>
              <strong className="font-medium text-foreground">Operational vendors:</strong> Secure client-relationship platforms, encrypted cloud storage providers, and merchant billing processors.
            </li>
            <li>
              <strong className="font-medium text-foreground">Regulatory authorities:</strong> Government agencies and border enforcement bodies when mandated by international travel statutes.
            </li>
          </ul>
        </>
  }, {
    id: "security-retention",
    title: "Data security and retention",
    icon: ShieldCheck,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            We enforce industry-standard technical and organizational measures to safeguard your information against unauthorized access, disclosure, alteration, and destruction. Client profiles and itinerary files are stored in encrypted environments with role-based access restrictions.
          </p>
          <p className="mt-3 text-foreground/90 leading-relaxed">
            Personal data is retained only as long as necessary to fulfill travel itineraries, satisfy accounting audits, or meet legal obligations, after which it is securely expunged or anonymized.
          </p>
        </>
  }, {
    id: "user-rights",
    title: "Your privacy rights",
    icon: Eye,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            Depending on your jurisdiction, you hold specific legal rights regarding your personal records, including:
          </p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-foreground/90">
            <li>
              <strong className="font-medium text-foreground">Access and correction:</strong> The right to request an extract of the personal records we hold about you and request amendments.
            </li>
            <li>
              <strong className="font-medium text-foreground">Deletion:</strong> The right to ask for the removal of your personal profile, subject to statutory record-keeping constraints.
            </li>
            <li>
              <strong className="font-medium text-foreground">Opt-out:</strong> The right to unsubscribe from non-transactional communications at any time.
            </li>
          </ul>
        </>
  }, {
    id: "children-policy-updates",
    title: "Children's privacy and policy updates",
    icon: RefreshCw,
    content: <>
          <p className="text-foreground/90 leading-relaxed">
            Because we coordinate multi-generational family journeys, we often receive passport and itinerary data for minors directly from parents or legal guardians. We never knowingly collect information directly from children under 16 without documented parental consent.
          </p>
          <p className="mt-3 text-foreground/90 leading-relaxed">
            We reserve the right to revise this Privacy Policy periodically. Substantive adjustments will be reflected on this page with an updated effective date.
          </p>
        </>
  }];
  return <section id="privacypolicy" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto max-w-4xl px-6">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mb-12 text-left">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="border-border text-foreground/80 px-3 py-1 text-xs uppercase tracking-wider font-medium">
              Legal notice
            </Badge>
            <span className="text-xs text-foreground/60">Last updated: October 2024</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
            Privacy policy
          </h1>
          <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-2xl leading-relaxed">
            How E•Leete Travel Design collects, protects, and handles your personal information across our bespoke travel curation services.
          </p>
        </motion.div>

        <Separator className="bg-border mb-12" />

        {/* Policy Content Sections */}
        <div className="space-y-12">
          {sections.map((section, index) => {
          const Icon = section.icon;
          return <motion.article key={section.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.04
          }} className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-accent/60 text-accent-foreground shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h2 className="font-serif text-xl sm:text-2xl font-medium text-foreground tracking-tight">
                    {section.title}
                  </h2>
                </div>

                <div className="text-sm sm:text-base leading-relaxed pl-1 sm:pl-11">
                  {section.content}
                </div>

                {index < sections.length - 1 && <div className="pt-8">
                    <Separator className="bg-border/60" />
                  </div>}
              </motion.article>;
        })}
        </div>

        {/* Contact Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-16 rounded-lg border border-border bg-card p-8 text-card-foreground shadow-xs">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="space-y-2">
              <h3 className="font-serif text-xl font-medium text-card-foreground">
                Questions or data requests
              </h3>
              <p className="text-sm text-card-foreground/80 max-w-xl">
                To request access to your travel profile records, correct details, or discuss our data protection practices, please contact our data stewardship team.
              </p>
            </div>

            <a href="mailto:info@e-leetetraveldesign.com" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-xs hover:bg-primary/90 transition-colors shrink-0">
              <Mail className="w-4 h-4" />
              <span>info@e-leetetraveldesign.com</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}